import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { makeFacade, makeRoof, makeAsphalt } from '../utils/cityFacade';
import { useOptionalTexture } from '../utils/useOptionalTexture';

// OsmCity — real Oakland, extruded from OpenStreetMap building footprints and
// surfaced with a triplanar facade (windows + floors on walls, gravel on
// roofs, windows that glow at night). Triplanar because these extruded
// footprints have no usable UVs — we project the facade from world position,
// so every wall gets believable windows at a consistent real-world scale.
//
// Standard WebGL, so it composes with the Spark splat interiors. Data lives at
// public/osm/oakland.geojson (a one-time Overpass export).
// © OpenStreetMap contributors (ODbL).

const GEOJSON_URL = '/osm/oakland.geojson';
const FIT_METRES = 150;
const FACADE_METRES = 40; // one facade tile spans ~40 m of building (bigger = less tiling)

function polygonsOf(feature) {
  const g = feature.geometry;
  if (!g) return [];
  if (g.type === 'Polygon') return [g.coordinates];
  if (g.type === 'MultiPolygon') return g.coordinates;
  return [];
}
function hashId(id) {
  let h = 2166136261; const s = String(id);
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h;
}
function heightOf(props, id) {
  if (props.height) { const h = parseFloat(props.height); if (!isNaN(h)) return Math.max(3, h); }
  const lv = parseFloat(props['building:levels']);
  if (!isNaN(lv)) return Math.max(3, lv * 3.3);
  return 7 + (hashId(id) % 9);
}
// Light near-white tints (used as a multiplier over the facade) so buildings
// vary in tone without muddying the texture.
function tintOf(id) {
  const hue = 0.05 + (hashId(id + 'h') % 30) / 1000;      // warm
  const sat = 0.04 + (hashId(id + 's') % 8) / 100;
  const lig = 0.55 + (hashId(id + 'l') % 38) / 100;         // 0.55–0.93
  return new THREE.Color().setHSL(hue, sat, lig);
}

function buildCity(data) {
  const feats = (data.features || []).filter((f) => f.properties && f.properties.building && polygonsOf(f).length);
  if (!feats.length) return null;
  let sumLon = 0, sumLat = 0, n = 0;
  for (const f of feats) for (const poly of polygonsOf(f)) for (const [lon, lat] of poly[0]) { sumLon += lon; sumLat += lat; n++; }
  const lon0 = sumLon / n, lat0 = sumLat / n;
  const mPerLon = 111320 * Math.cos((lat0 * Math.PI) / 180), mPerLat = 110540;
  const project = ([lon, lat]) => [(lon - lon0) * mPerLon, -(lat - lat0) * mPerLat];

  const geos = [];
  for (const f of feats) {
    const h = heightOf(f.properties, f.id);
    const col = tintOf(f.id);
    for (const poly of polygonsOf(f)) {
      const outer = poly[0].map(project);
      if (outer.length < 3) continue;
      const shape = new THREE.Shape();
      outer.forEach(([px, pz], i) => (i ? shape.lineTo(px, pz) : shape.moveTo(px, pz)));
      for (let hi = 1; hi < poly.length; hi++) {
        const ring = poly[hi].map(project);
        if (ring.length < 3) continue;
        const path = new THREE.Path();
        ring.forEach(([px, pz], i) => (i ? path.lineTo(px, pz) : path.moveTo(px, pz)));
        shape.holes.push(path);
      }
      let geo;
      try { geo = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false, steps: 1 }); } catch { continue; }
      geo.rotateX(-Math.PI / 2);
      const cnt = geo.attributes.position.count;
      const colors = new Float32Array(cnt * 3);
      for (let i = 0; i < cnt; i++) { colors[i * 3] = col.r; colors[i * 3 + 1] = col.g; colors[i * 3 + 2] = col.b; }
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geos.push(geo);
    }
  }
  if (!geos.length) return null;
  const merged = mergeGeometries(geos, false);
  geos.forEach((g) => g.dispose());
  if (!merged) return null;
  merged.computeBoundingBox();
  const bb = merged.boundingBox;
  const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
  const span = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z) || 1;
  const scale = FIT_METRES / span;
  return { geometry: merged, scale, pos: [-cx * scale, 0, -cz * scale], count: feats.length };
}

export default function OsmCity({ night = 0 }) {
  const [data, setData] = useState(undefined);
  const nightRef = useRef(night);
  nightRef.current = night;

  useEffect(() => {
    let alive = true;
    fetch(GEOJSON_URL).then((r) => (r.ok ? r.json() : null)).then((d) => alive && setData(d)).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, []);

  // Gemini Pro city textures when present (public/textures/city_*.jpg),
  // procedural canvas fallback until they load. Triplanar tiles them, so the
  // generator wrap-blends the edges to keep them seamless.
  const proc = useMemo(() => ({ facade: makeFacade(), roof: makeRoof(), asphalt: makeAsphalt() }), []);
  useEffect(() => () => Object.values(proc).forEach((t) => t.dispose()), [proc]);
  const facadeTex = useOptionalTexture('/textures/city_facade.jpg', proc.facade);
  const roofTex = useOptionalTexture('/textures/city_roof.jpg', proc.roof);
  const asphaltTex = useOptionalTexture('/textures/asphalt.jpg', proc.asphalt);

  const city = useMemo(() => (data ? buildCity(data) : null), [data]);

  // Triplanar facade material — projects facade on vertical faces, roof on
  // horizontal, and adds glowing windows scaled by the night factor.
  const material = useMemo(() => {
    const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.78, metalness: 0.05, envMapIntensity: 0.85 });
    const texScale = city ? 1 / (FACADE_METRES * city.scale) : 0.14;
    m.onBeforeCompile = (shader) => {
      shader.uniforms.uFacade = { value: facadeTex };
      shader.uniforms.uRoof = { value: roofTex };
      shader.uniforms.uTexScale = { value: texScale };
      shader.uniforms.uNight = { value: nightRef.current };
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;\nvarying vec3 vWN;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvWN = normalize(mat3(modelMatrix) * objectNormal);');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
          varying vec3 vWPos; varying vec3 vWN;
          uniform sampler2D uFacade; uniform sampler2D uRoof;
          uniform float uTexScale; uniform float uNight;`)
        .replace('#include <map_fragment>', `
          vec3 bw = pow(abs(vWN), vec3(6.0)); bw /= (bw.x + bw.y + bw.z + 1e-4);
          vec3 wall = texture2D(uFacade, vWPos.zy * uTexScale).rgb * bw.x
                    + texture2D(uFacade, vWPos.xy * uTexScale).rgb * bw.z;
          vec3 roofc = texture2D(uRoof, vWPos.xz * uTexScale * 0.5).rgb * bw.y;
          diffuseColor.rgb *= (wall + roofc);`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          // Night glow derived from the facade itself: dark glass → lit window.
          // Self-aligns with whatever facade texture is loaded (Gemini or proc).
          vec3 bwe = pow(abs(vWN), vec3(6.0)); bwe /= (bwe.x + bwe.y + bwe.z + 1e-4);
          float wallAmt = bwe.x + bwe.z;
          vec3 wcol = texture2D(uFacade, vWPos.zy * uTexScale).rgb * bwe.x
                    + texture2D(uFacade, vWPos.xy * uTexScale).rgb * bwe.z;
          float lum = dot(wcol, vec3(0.299, 0.587, 0.114));
          float win = smoothstep(0.34, 0.10, lum);
          totalEmissiveRadiance += vec3(1.0, 0.72, 0.36) * win * wallAmt * uNight * 1.7;`);
      m.userData.shader = shader;
    };
    m.customProgramCacheKey = () => 'osm-facade-v3';
    return m;
  }, [facadeTex, roofTex, city]);

  // Keep the night glow synced to the world clock.
  useFrame(() => {
    const sh = material.userData.shader;
    if (sh) sh.uniforms.uNight.value = nightRef.current;
  });

  const asphalt = useMemo(() => {
    const t = asphaltTex.clone(); t.needsUpdate = true;
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(60, 60);
    return t;
  }, [asphaltTex]);

  useEffect(() => {
    if (data === null) console.warn(`[osm] no ${GEOJSON_URL} — save the Overpass buildings export there.`);
    else if (data && city) console.log(`[osm] extruded ${city.count} Oakland buildings (scale ${city.scale.toFixed(3)})`);
  }, [data, city]);

  return (
    <group>
      <hemisphereLight args={['#cbd5e1', '#241f19', 0.35]} />
      {/* Gritty street — low roughness gives it a damp sheen off the environment */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]}>
        <planeGeometry args={[800, 800]} />
        <meshStandardMaterial map={asphalt} color="#8a8a90" roughness={0.55} metalness={0.2} envMapIntensity={0.6} />
      </mesh>
      {city && (
        <mesh geometry={city.geometry} material={material} position={city.pos} scale={city.scale} receiveShadow />
      )}
    </group>
  );
}
